import { Fragment, useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";

import RaceSelector from "../components/RaceSelector/RaceSelector";
import BlockTitle from "../components/Common/BlockTitle";
import PageHeading from "../components/Common/PageHeading";
import { RaceResults } from "../components/RaceResults";
import StandingsTables from "../components/SeasonTable/StandingsTables";

import { useRacesContext } from "../contexts/races";
import { useStandingsContext } from "../contexts/standings";
import { useScheduleContext } from "../contexts/schedule";
import ResultsSEO from "../components/SEO/ResultsSEO";
import { Pill } from "./HallOfFame/styles";

const VIEWS = [
  { key: "races", title: "Етапи" },
  { key: "standings", title: "Залік" },
];

// Same control the Hall of Fame uses for its session filter
const ViewSwitch = styled.div`
  display: flex;
  gap: 8px;
  /* Optically centred against the title's cap height: flex centring is mathematically correct
     but reads a touch high next to 28px uppercase text. */
  transform: translateY(2px);
`;

const ResultsMainPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [raceId, setRaceId] = useState(null);
  const [seasonId, setSeasonId] = useState(null);
  const [selectedRaceType, setSelectedRaceType] = useState({ key: "Race" });
  const { races, loadRace } = useRacesContext();
  const { loadSeason } = useStandingsContext();
  const { seasons } = useScheduleContext();

  const view = searchParams.get("view") === "standings" ? "standings" : "races";
  const group = searchParams.get("group") === "teams" ? "teams" : "drivers";

  const setParam = useCallback(
    (key, value) => {
      // `r` is deliberately left alone: switching views is a toggle, and coming back should
      // land on the race you were looking at.
      searchParams.set(key, value);
      setSearchParams(searchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    if (raceId) {
      loadRace(raceId);
    }
  }, [loadRace, raceId]);

  useEffect(() => {
    if (view === "standings" && seasonId) {
      loadSeason(seasonId);
    }
  }, [view, seasonId, loadSeason]);

  return (
    <Fragment>
      <ResultsSEO season={seasonId ? seasons[seasonId] : null} />
      <div style={{ marginTop: 24 }}>
        <BlockTitle>
          <PageHeading>Результати</PageHeading>
          <ViewSwitch role="radiogroup" aria-label="Режим перегляду">
            {VIEWS.map((item) => (
              <Pill
                key={item.key}
                role="radio"
                aria-checked={view === item.key}
                $active={view === item.key}
                onClick={() => setParam("view", item.key)}
              >
                {item.title}
              </Pill>
            ))}
          </ViewSwitch>
        </BlockTitle>

        {/* Keyed on view so the selector remounts when you switch. Its mount-time branch is the
            one that reads `r`/`s` from the URL, and a round cell in the standings view links back
            into this same route -- without a remount that click would never change the race. */}
        <RaceSelector
          key={view}
          view={view}
          group={group}
          onRaceChange={setRaceId}
          onRaceTypeChange={setSelectedRaceType}
          onSeasonChange={setSeasonId}
          onGroupChange={(value) => setParam("group", value)}
        />

        {view === "races" && raceId && (
          <RaceResults race={races[raceId]} type={selectedRaceType.key} />
        )}

        {view === "standings" && seasonId && (
          <StandingsTables seasonId={seasonId} group={group} />
        )}
      </div>
    </Fragment>
  );
};

export default ResultsMainPage;
